import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { Box, Collapse, Divider, Menu, MenuItem } from '@mui/material';
import clsx from 'clsx';
import Tooltip from 'components/Tooltip';
import { adminRole } from 'helper/constant';
import { getAdminDetails, getInitials } from 'helper/utility';
import PropTypes from 'prop-types';
import routeEndpoints from 'route/endpoints';

import styles from '../sidebar.module.css';

import { ReactComponent as ChevronRight } from 'asset/IconsPack/at_chevron-right-filled.svg';
import { ReactComponent as LogoutIcon } from 'asset/IconsPack/at_log-out-03.svg';
import { ReactComponent as UserCircleIcon } from 'asset/IconsPack/at_user-circle.svg';

import LogoutConfirmDialog from './LogoutConfirmDialog';

function AccountMenu({ collapsed, inline, user, adminData }) {
    const history = useHistory();
    const [anchorEl, setAnchorEl] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLogoutOpen, setIsLogoutOpen] = useState(false);
    const isOpen = Boolean(anchorEl);
    const isShown = inline ? isExpanded : isOpen;
    const listId = inline ? 'account-sub-nav' : 'account-menu';
    const { fullName = '', email } = getAdminDetails(user?.company, adminData);
    const initials = getInitials(fullName);
    const role = adminRole[adminData?.type];

    const closeMenu = () => setAnchorEl(null);

    const handleTriggerClick = (event) => {
        if (inline) {
            setIsExpanded(!isExpanded);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleLogout = () => {
        closeMenu();
        setIsLogoutOpen(true);
    };

    return (
        <>
            <Tooltip
                title={collapsed && !isOpen ? fullName : ''}
                placement="right"
                classes={{ tooltip: styles.collapseTooltip }}
            >
                <button
                    type="button"
                    className={clsx(
                        styles.navItem,
                        styles.navItemButton,
                        styles.accountTrigger,
                        collapsed && styles.navItemCollapsed,
                        isOpen && styles.accountTriggerOpen
                    )}
                    onClick={handleTriggerClick}
                    aria-label={collapsed ? `Account, ${fullName}` : undefined}
                    aria-haspopup={inline ? undefined : 'menu'}
                    aria-expanded={isShown}
                    aria-controls={isShown ? listId : undefined}
                >
                    <span className={styles.avatar} aria-hidden="true">
                        {initials}
                    </span>
                    {!collapsed && (
                        <>
                            <span className={styles.accountText}>
                                <span className={styles.accountName}>{fullName}</span>
                                <span className={styles.accountMeta}>{role}</span>
                            </span>
                            <ChevronRight
                                className={clsx(
                                    styles.navChevron,
                                    isExpanded && styles.navChevronOpen
                                )}
                                aria-hidden="true"
                            />
                        </>
                    )}
                </button>
            </Tooltip>
            {inline && (
                <Collapse in={isExpanded} id={listId}>
                    <Box className={styles.subNav}>
                        <Link
                            to={{ pathname: routeEndpoints.settings, hash: 'profile' }}
                            className={styles.subNavItem}
                        >
                            Profile
                        </Link>
                        <button
                            type="button"
                            className={clsx(
                                styles.subNavItem,
                                styles.navItemButton,
                                styles.logoutItem
                            )}
                            onClick={handleLogout}
                        >
                            Logout
                        </button>
                    </Box>
                </Collapse>
            )}
            <Menu
                id="account-menu"
                open={isOpen}
                anchorEl={anchorEl}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                classes={{ paper: styles.flyoutPaper, list: styles.footerMenuList }}
            >
                <MenuItem disabled className={styles.accountHeader}>
                    <span className={clsx(styles.avatar, styles.avatarLarge)} aria-hidden="true">
                        {initials}
                    </span>
                    <span className={styles.accountText}>
                        <span className={styles.accountName}>{fullName}</span>
                        <span className={styles.accountMeta}>{email}</span>
                    </span>
                </MenuItem>
                <Divider className={styles.accountDivider} />
                <MenuItem
                    component={Link}
                    to={{ pathname: routeEndpoints.settings, hash: 'profile' }}
                    className={styles.footerMenuItem}
                    onClick={closeMenu}
                >
                    <UserCircleIcon /> Profile
                </MenuItem>
                <MenuItem
                    className={clsx(styles.footerMenuItem, styles.logoutItem)}
                    onClick={handleLogout}
                >
                    <LogoutIcon /> Logout
                </MenuItem>
            </Menu>
            <LogoutConfirmDialog
                open={isLogoutOpen}
                onClose={() => setIsLogoutOpen(false)}
                onConfirm={() => history.push(routeEndpoints.logout)}
            />
        </>
    );
}

AccountMenu.propTypes = {
    collapsed: PropTypes.bool,
    inline: PropTypes.bool,
    user: PropTypes.object,
    adminData: PropTypes.object,
};

function mapStateToProps(state) {
    return {
        user: state.auth?.userData?.user,
        adminData: state.auth?.adminData,
    };
}

export default connect(mapStateToProps)(AccountMenu);
